import type {
  PublicStoreWebsiteDto,
  WebsiteInterfaceLocale,
  WebsitePageKind,
} from '../../types/website-templates';
import { WEBSITE_INTERFACE_LOCALES } from '../../types/website-templates';
import { WEBSITE_CANVAS_PATH_MAX_LENGTH, parseWebsiteCanvasStorePath } from './canvas-routes';
import { isWebsiteDraftBrowseToken } from './draft-browse';
import { isWebsiteEditRegionId } from './edit-regions';
import { isOneOf, isRecord, type UnknownRecord } from './internal';
import { isWebsitePageKind } from './page-kinds';
import { parsePublicStoreWebsiteDto } from './public-dto';
import { checkMerchantText } from './text-safety';

/**
 * C8 website canvas bridge, protocol v2 (A19). A separate protocol id from
 * the retired builder canvas, so a builder message can never be mistaken for
 * a website message. v1 messages are refused.
 *
 * The iframe never receives slot schemas, manifests, revision ids or values
 * of other stores. It can only ASK the host to edit a region id; Admin
 * re-checks that id against the pinned manifest and the last region report
 * before it opens a dialog. The canvas has no write API.
 */
export const WEBSITE_CANVAS_PROTOCOL = 'dukanti.website.canvas';
export const WEBSITE_CANVAS_PROTOCOL_VERSION = 2;
export const WEBSITE_CANVAS_REGION_LABEL_MAX_LENGTH = 120;
export const WEBSITE_CANVAS_MAX_REGIONS = 120;
export const WEBSITE_CANVAS_PATH_SUFFIX = 'website-canvas';
/** CSS px. A rect further out than this is not a layout, it is a mistake. */
export const WEBSITE_CANVAS_RECT_LIMIT = 1_000_000;
/** The host replaces a grant at least this long before it expires. */
export const WEBSITE_CANVAS_GRANT_REFRESH_LEAD_MS = 5 * 60_000;
/** The canvas reports regions no more often than this. */
export const WEBSITE_CANVAS_REGION_REPORT_INTERVAL_MS = 250;

const SESSION_VALUE_RE = /^[A-Za-z0-9-]{8,80}$/;
const ISO_INSTANT_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|[+-]\d{2}:\d{2})$/;
const STORE_SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const STORE_SLUG_MAX_LENGTH = 63;

export interface WebsiteCanvasSession {
  readonly sessionId: string;
  readonly nonce: string;
}

interface MessageBase {
  readonly protocol: typeof WEBSITE_CANVAS_PROTOCOL;
  readonly version: typeof WEBSITE_CANVAS_PROTOCOL_VERSION;
}

/** `store-draft`: the merchant's real routed storefront. `template-demo`: the gallery Preview on fixtures. */
export type WebsiteCanvasMode = 'store-draft' | 'template-demo';
export const WEBSITE_CANVAS_MODES = [
  'store-draft',
  'template-demo',
] as const satisfies readonly WebsiteCanvasMode[];

/** `label` is the finished accessible name in the ADMIN language ("Edit hero text 2"). */
export interface WebsiteCanvasRegionChrome {
  readonly regionId: string;
  readonly label: string;
}

/** Already localized by Admin. */
export interface WebsiteCanvasOverlayLabels {
  readonly moreItems: string;
  readonly regionListTitle: string;
}

/** CSS px in document coordinates. */
export interface WebsiteCanvasRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Public store identity for the template header and footer. The canvas needs
 * it from the host because a store being set up may not be public yet.
 */
export interface WebsiteCanvasStoreIdentity {
  readonly name: string;
  /** An image-proxy URL, or null. Never a raw storage URL. */
  readonly logoUrl: string | null;
  readonly whatsappNumber: string | null;
  readonly phoneNumber: string | null;
}

export interface WebsiteCanvasPagePayload {
  readonly pageKind: WebsitePageKind;
  /** Monotonic per host session. It grows with every draft revision change. */
  readonly generation: number;
  /** Wire form (opaque media references), mode `template`, `page.kind === pageKind`. */
  readonly dto: PublicStoreWebsiteDto;
  readonly store: WebsiteCanvasStoreIdentity;
  /** Every region of chrome and this page. `[]` in `template-demo`. */
  readonly regions: readonly WebsiteCanvasRegionChrome[];
}

/** Every region of the page is reported. An unanchored region is never measured. */
export type WebsiteCanvasRegionReport =
  | {
      readonly regionId: string;
      readonly anchored: true;
      readonly rect: WebsiteCanvasRect;
      readonly visible: boolean;
    }
  | {
      readonly regionId: string;
      readonly anchored: false;
      readonly rect: null;
      readonly visible: false;
    };

/** C14. Lives in memory only, on both sides. */
export interface WebsiteCanvasDraftBrowseGrant {
  /** 64 lowercase hex characters. */
  readonly token: string;
  /** ISO 8601 instant. */
  readonly expiresAt: string;
}

export type WebsiteCanvasNavigateTarget =
  /** Must pass `parseWebsiteCanvasStorePath` for the store of the session. */
  | { readonly kind: 'path'; readonly path: string }
  /** The canvas picks the real route (`product`: the first public product, else the categories). */
  | { readonly kind: 'page'; readonly pageKind: WebsitePageKind }
  | { readonly kind: 'history'; readonly direction: 'back' | 'forward' };

export interface WebsiteCanvasHostInitMessage extends MessageBase, WebsiteCanvasSession {
  readonly type: 'host.websiteInit';
  readonly storeSlug: string;
  readonly mode: WebsiteCanvasMode;
  readonly adminLocale: WebsiteInterfaceLocale;
  readonly initialPath: string;
  readonly editMode: boolean;
  readonly overlayLabels: WebsiteCanvasOverlayLabels;
  /** A grant in `store-draft`, null in `template-demo`. */
  readonly draftBrowseGrant: WebsiteCanvasDraftBrowseGrant | null;
}
export interface WebsiteCanvasHostDraftBrowseGrantMessage
  extends MessageBase, WebsiteCanvasSession {
  readonly type: 'host.draftBrowseGrant';
  readonly grant: WebsiteCanvasDraftBrowseGrant;
}
export interface WebsiteCanvasHostPageMessage extends MessageBase, WebsiteCanvasSession {
  readonly type: 'host.page';
  readonly sequence: number;
  readonly payload: WebsiteCanvasPagePayload;
}
export interface WebsiteCanvasHostEditModeMessage extends MessageBase, WebsiteCanvasSession {
  readonly type: 'host.editMode';
  readonly enabled: boolean;
}
export interface WebsiteCanvasHostNavigateMessage extends MessageBase, WebsiteCanvasSession {
  readonly type: 'host.navigate';
  readonly target: WebsiteCanvasNavigateTarget;
}
export interface WebsiteCanvasHostFocusRegionMessage extends MessageBase, WebsiteCanvasSession {
  readonly type: 'host.focusRegion';
  readonly regionId: string;
}
export interface WebsiteCanvasHostInteractionLockMessage extends MessageBase, WebsiteCanvasSession {
  readonly type: 'host.interactionLock';
  readonly locked: boolean;
}
export type WebsiteCanvasHostToCanvasMessage =
  | WebsiteCanvasHostInitMessage
  | WebsiteCanvasHostDraftBrowseGrantMessage
  | WebsiteCanvasHostPageMessage
  | WebsiteCanvasHostEditModeMessage
  | WebsiteCanvasHostNavigateMessage
  | WebsiteCanvasHostFocusRegionMessage
  | WebsiteCanvasHostInteractionLockMessage;

export interface WebsiteCanvasReadyMessage extends MessageBase {
  readonly type: 'canvas.websiteReady';
  readonly canvasInstanceId: string;
}
export interface WebsiteCanvasAckMessage extends MessageBase, WebsiteCanvasSession {
  readonly type: 'canvas.websiteAck';
  readonly sequence: number;
}
export interface WebsiteCanvasRouteChangedMessage extends MessageBase, WebsiteCanvasSession {
  readonly type: 'canvas.routeChanged';
  /** Canonical store path when `pageKind` is set; any safe in-app path otherwise. */
  readonly path: string;
  /** Null: the route is not part of the storefront editor. */
  readonly pageKind: WebsitePageKind | null;
  readonly canGoBack: boolean;
  readonly canGoForward: boolean;
}
export interface WebsiteCanvasRegionsChangedMessage extends MessageBase, WebsiteCanvasSession {
  readonly type: 'canvas.regionsChanged';
  readonly pageKind: WebsitePageKind;
  readonly regions: readonly WebsiteCanvasRegionReport[];
}
export interface WebsiteCanvasRegionEditIntentMessage extends MessageBase, WebsiteCanvasSession {
  readonly type: 'canvas.regionEditIntent';
  readonly regionId: string;
}
export interface WebsiteCanvasNavigationBlockedMessage extends MessageBase, WebsiteCanvasSession {
  readonly type: 'canvas.navigationBlocked';
  readonly reason: 'locked';
}
export type WebsiteCanvasErrorCode = 'invalid_payload' | 'unsupported_template' | 'render_failed';
export interface WebsiteCanvasErrorMessage extends MessageBase {
  readonly type: 'canvas.websiteError';
  readonly code: WebsiteCanvasErrorCode;
}
export type WebsiteCanvasCanvasToHostMessage =
  | WebsiteCanvasReadyMessage
  | WebsiteCanvasAckMessage
  | WebsiteCanvasRouteChangedMessage
  | WebsiteCanvasRegionsChangedMessage
  | WebsiteCanvasRegionEditIntentMessage
  | WebsiteCanvasNavigationBlockedMessage
  | WebsiteCanvasErrorMessage;

/**
 * The store both sides talk about. A parser given it refuses every path of
 * another store. Without it a path is only checked against the store it
 * names itself, and the receiver MUST compare with its own slug
 * (`parseWebsiteCanvasStorePath(path, ownSlug)`) before it acts.
 */
export interface WebsiteCanvasParseOptions {
  readonly storeSlug?: string;
}

const BASE_KEYS = ['protocol', 'version', 'type'] as const;
const SESSION_KEYS = [...BASE_KEYS, 'sessionId', 'nonce'] as const;
const ERROR_CODES: readonly WebsiteCanvasErrorCode[] = [
  'invalid_payload',
  'unsupported_template',
  'render_failed',
];

function hasExactKeys(value: UnknownRecord, keys: readonly string[]): boolean {
  const actual = Object.keys(value);
  return actual.length === keys.length && keys.every((key) => actual.includes(key));
}

function isBase(value: UnknownRecord): boolean {
  return (
    value.protocol === WEBSITE_CANVAS_PROTOCOL && value.version === WEBSITE_CANVAS_PROTOCOL_VERSION
  );
}

function hasSession(value: UnknownRecord): boolean {
  return (
    typeof value.sessionId === 'string' &&
    SESSION_VALUE_RE.test(value.sessionId) &&
    typeof value.nonce === 'string' &&
    SESSION_VALUE_RE.test(value.nonce)
  );
}

function isStoreSlug(value: unknown): value is string {
  return (
    typeof value === 'string' && value.length <= STORE_SLUG_MAX_LENGTH && STORE_SLUG_RE.test(value)
  );
}

function isSequence(value: unknown): value is number {
  return Number.isSafeInteger(value) && (value as number) >= 0;
}

function base(): MessageBase {
  return { protocol: WEBSITE_CANVAS_PROTOCOL, version: WEBSITE_CANVAS_PROTOCOL_VERSION };
}

const E164_RE = /^\+[1-9][0-9]{7,14}$/;
const IMAGE_PROXY_PATH = '/functions/v1/image-proxy';
export const WEBSITE_CANVAS_STORE_NAME_MAX_LENGTH = 120;

function isLocalHostname(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

function parseStoreIdentity(value: unknown): WebsiteCanvasStoreIdentity | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ['name', 'logoUrl', 'whatsappNumber', 'phoneNumber'])
  ) {
    return null;
  }
  const name = checkMerchantText(value.name, { maxLength: WEBSITE_CANVAS_STORE_NAME_MAX_LENGTH });
  if (!name.ok) return null;

  let logoUrl: string | null = null;
  if (value.logoUrl !== null) {
    if (typeof value.logoUrl !== 'string' || value.logoUrl.length > 1024) return null;
    try {
      const parsed = new URL(value.logoUrl);
      const secure =
        parsed.protocol === 'https:' ||
        (parsed.protocol === 'http:' && isLocalHostname(parsed.hostname));
      if (!secure || parsed.username || parsed.password) return null;
      if (!parsed.pathname.endsWith(IMAGE_PROXY_PATH)) return null;
      logoUrl = parsed.toString();
    } catch {
      return null;
    }
  }

  const phone = (raw: unknown): string | null | undefined => {
    if (raw === null) return null;
    return typeof raw === 'string' && E164_RE.test(raw) ? raw : undefined;
  };
  const whatsappNumber = phone(value.whatsappNumber);
  const phoneNumber = phone(value.phoneNumber);
  if (whatsappNumber === undefined || phoneNumber === undefined) return null;

  return { name: value.name as string, logoUrl, whatsappNumber, phoneNumber };
}

function parseLabel(value: unknown): string | null {
  const checked = checkMerchantText(value, { maxLength: WEBSITE_CANVAS_REGION_LABEL_MAX_LENGTH });
  return checked.ok ? (value as string) : null;
}

export function parseWebsiteCanvasDraftBrowseGrant(
  value: unknown,
): WebsiteCanvasDraftBrowseGrant | null {
  if (!isRecord(value) || !hasExactKeys(value, ['token', 'expiresAt'])) return null;
  if (!isWebsiteDraftBrowseToken(value.token)) return null;
  if (typeof value.expiresAt !== 'string' || !ISO_INSTANT_RE.test(value.expiresAt)) return null;
  if (!Number.isFinite(Date.parse(value.expiresAt))) return null;
  return { token: value.token, expiresAt: value.expiresAt };
}

function parseOverlayLabels(value: unknown): WebsiteCanvasOverlayLabels | null {
  if (!isRecord(value) || !hasExactKeys(value, ['moreItems', 'regionListTitle'])) return null;
  const moreItems = parseLabel(value.moreItems);
  const regionListTitle = parseLabel(value.regionListTitle);
  if (moreItems === null || regionListTitle === null) return null;
  return { moreItems, regionListTitle };
}

export function parseWebsiteCanvasPagePayload(value: unknown): WebsiteCanvasPagePayload | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ['pageKind', 'generation', 'dto', 'store', 'regions'])
  ) {
    return null;
  }
  if (!isWebsitePageKind(value.pageKind) || !isSequence(value.generation)) return null;
  const store = parseStoreIdentity(value.store);
  if (!store) return null;

  const parsed = parsePublicStoreWebsiteDto(value.dto);
  if (!parsed.ok || parsed.dto.mode !== 'template' || parsed.dto.page?.kind !== value.pageKind) {
    return null;
  }
  if (!Array.isArray(value.regions) || value.regions.length > WEBSITE_CANVAS_MAX_REGIONS) {
    return null;
  }

  // A region of an empty optional slot renders nothing but stays editable, so
  // a region may name slots absent from the DTO. The host re-checks every
  // region id it receives back.
  const regions: WebsiteCanvasRegionChrome[] = [];
  const seen = new Set<string>();
  for (const item of value.regions) {
    if (!isRecord(item) || !hasExactKeys(item, ['regionId', 'label'])) return null;
    if (!isWebsiteEditRegionId(item.regionId) || seen.has(item.regionId)) return null;
    const label = parseLabel(item.label);
    if (label === null) return null;
    seen.add(item.regionId);
    regions.push({ regionId: item.regionId, label });
  }
  return {
    pageKind: value.pageKind,
    generation: value.generation,
    dto: parsed.dto,
    store,
    regions,
  };
}

/** The slug a `/s/<slug>…` path names, or null. */
function slugOfPath(path: string): string | null {
  const match = /^\/s\/([^/?#]+)/.exec(path);
  const slug = match?.[1];
  return isStoreSlug(slug) ? slug : null;
}

function parseStorePath(
  raw: unknown,
  options: WebsiteCanvasParseOptions,
): ReturnType<typeof parseWebsiteCanvasStorePath> {
  if (typeof raw !== 'string') return null;
  const storeSlug = options.storeSlug ?? slugOfPath(raw);
  return storeSlug === null ? null : parseWebsiteCanvasStorePath(raw, storeSlug);
}

/** A path inside the app that is not a store route: what "not in the editor" reports. */
function isSafeAppPath(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  if (value.length === 0 || value.length > WEBSITE_CANVAS_PATH_MAX_LENGTH) return false;
  if (!value.startsWith('/') || value.startsWith('//')) return false;
  if (value.includes('\\') || value.includes('#') || value.includes('..')) return false;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code <= 0x20 || code === 0x7f) return false;
  }
  return true;
}

function parseNavigateTarget(
  value: unknown,
  options: WebsiteCanvasParseOptions,
): WebsiteCanvasNavigateTarget | null {
  if (!isRecord(value)) return null;
  if (value.kind === 'path') {
    if (!hasExactKeys(value, ['kind', 'path'])) return null;
    const parsed = parseStorePath(value.path, options);
    return parsed ? { kind: 'path', path: parsed.path } : null;
  }
  if (value.kind === 'page') {
    if (!hasExactKeys(value, ['kind', 'pageKind']) || !isWebsitePageKind(value.pageKind)) {
      return null;
    }
    return { kind: 'page', pageKind: value.pageKind };
  }
  if (value.kind === 'history') {
    if (!hasExactKeys(value, ['kind', 'direction'])) return null;
    if (value.direction !== 'back' && value.direction !== 'forward') return null;
    return { kind: 'history', direction: value.direction };
  }
  return null;
}

function parseInit(
  value: UnknownRecord,
  session: WebsiteCanvasSession,
  options: WebsiteCanvasParseOptions,
): WebsiteCanvasHostInitMessage | null {
  if (
    !hasExactKeys(value, [
      ...SESSION_KEYS,
      'storeSlug',
      'mode',
      'adminLocale',
      'initialPath',
      'editMode',
      'overlayLabels',
      'draftBrowseGrant',
    ])
  ) {
    return null;
  }
  if (!isStoreSlug(value.storeSlug)) return null;
  if (options.storeSlug !== undefined && options.storeSlug !== value.storeSlug) return null;
  if (!isOneOf(WEBSITE_CANVAS_MODES, value.mode)) return null;
  if (!isOneOf(WEBSITE_INTERFACE_LOCALES, value.adminLocale)) return null;
  if (typeof value.editMode !== 'boolean') return null;

  const initialPath = parseWebsiteCanvasStorePath(value.initialPath, value.storeSlug);
  const overlayLabels = parseOverlayLabels(value.overlayLabels);
  if (!initialPath || !overlayLabels) return null;

  let draftBrowseGrant: WebsiteCanvasDraftBrowseGrant | null = null;
  if (value.mode === 'store-draft') {
    draftBrowseGrant = parseWebsiteCanvasDraftBrowseGrant(value.draftBrowseGrant);
    if (!draftBrowseGrant) return null;
  } else if (value.draftBrowseGrant !== null) {
    // A demo shows fixtures. It never holds a key to a real catalog.
    return null;
  }

  return {
    ...base(),
    type: 'host.websiteInit',
    ...session,
    storeSlug: value.storeSlug,
    mode: value.mode,
    adminLocale: value.adminLocale,
    initialPath: initialPath.path,
    editMode: value.editMode,
    overlayLabels,
    draftBrowseGrant,
  };
}

export function parseWebsiteCanvasHostMessage(
  value: unknown,
  options: WebsiteCanvasParseOptions = {},
): WebsiteCanvasHostToCanvasMessage | null {
  if (!isRecord(value) || !isBase(value) || !hasSession(value)) return null;
  const session = { sessionId: value.sessionId as string, nonce: value.nonce as string };

  switch (value.type) {
    case 'host.websiteInit':
      return parseInit(value, session, options);
    case 'host.draftBrowseGrant': {
      if (!hasExactKeys(value, [...SESSION_KEYS, 'grant'])) return null;
      const grant = parseWebsiteCanvasDraftBrowseGrant(value.grant);
      return grant ? { ...base(), type: 'host.draftBrowseGrant', ...session, grant } : null;
    }
    case 'host.page': {
      if (!hasExactKeys(value, [...SESSION_KEYS, 'sequence', 'payload'])) return null;
      if (!isSequence(value.sequence)) return null;
      const payload = parseWebsiteCanvasPagePayload(value.payload);
      if (!payload) return null;
      return { ...base(), type: 'host.page', ...session, sequence: value.sequence, payload };
    }
    case 'host.editMode':
      if (!hasExactKeys(value, [...SESSION_KEYS, 'enabled'])) return null;
      if (typeof value.enabled !== 'boolean') return null;
      return { ...base(), type: 'host.editMode', ...session, enabled: value.enabled };
    case 'host.navigate': {
      if (!hasExactKeys(value, [...SESSION_KEYS, 'target'])) return null;
      const target = parseNavigateTarget(value.target, options);
      return target ? { ...base(), type: 'host.navigate', ...session, target } : null;
    }
    case 'host.focusRegion':
      if (!hasExactKeys(value, [...SESSION_KEYS, 'regionId'])) return null;
      if (!isWebsiteEditRegionId(value.regionId)) return null;
      return { ...base(), type: 'host.focusRegion', ...session, regionId: value.regionId };
    case 'host.interactionLock':
      if (!hasExactKeys(value, [...SESSION_KEYS, 'locked'])) return null;
      if (typeof value.locked !== 'boolean') return null;
      return { ...base(), type: 'host.interactionLock', ...session, locked: value.locked };
    default:
      return null;
  }
}

function isRectNumber(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    Math.abs(value) <= WEBSITE_CANVAS_RECT_LIMIT
  );
}

function parseRect(value: unknown): WebsiteCanvasRect | null {
  if (!isRecord(value) || !hasExactKeys(value, ['x', 'y', 'width', 'height'])) return null;
  const { x, y, width, height } = value;
  if (!isRectNumber(x) || !isRectNumber(y) || !isRectNumber(width) || !isRectNumber(height)) {
    return null;
  }
  if (width < 0 || height < 0) return null;
  return { x, y, width, height };
}

function parseRegionReport(value: unknown): WebsiteCanvasRegionReport | null {
  if (!isRecord(value) || !hasExactKeys(value, ['regionId', 'anchored', 'rect', 'visible'])) {
    return null;
  }
  if (!isWebsiteEditRegionId(value.regionId)) return null;
  if (value.anchored === true) {
    const rect = parseRect(value.rect);
    if (!rect || typeof value.visible !== 'boolean') return null;
    return { regionId: value.regionId, anchored: true, rect, visible: value.visible };
  }
  if (value.anchored !== false || value.rect !== null || value.visible !== false) return null;
  return { regionId: value.regionId, anchored: false, rect: null, visible: false };
}

function parseRouteChanged(
  value: UnknownRecord,
  session: WebsiteCanvasSession,
  options: WebsiteCanvasParseOptions,
): WebsiteCanvasRouteChangedMessage | null {
  if (
    !hasExactKeys(value, [...SESSION_KEYS, 'path', 'pageKind', 'canGoBack', 'canGoForward']) ||
    typeof value.canGoBack !== 'boolean' ||
    typeof value.canGoForward !== 'boolean'
  ) {
    return null;
  }
  const history = { canGoBack: value.canGoBack, canGoForward: value.canGoForward };

  if (value.pageKind === null) {
    if (!isSafeAppPath(value.path)) return null;
    // A route of this store always has a page kind.
    if (
      options.storeSlug !== undefined &&
      parseWebsiteCanvasStorePath(value.path, options.storeSlug)
    ) {
      return null;
    }
    return {
      ...base(),
      type: 'canvas.routeChanged',
      ...session,
      path: value.path,
      pageKind: null,
      ...history,
    };
  }

  const parsed = parseStorePath(value.path, options);
  if (!parsed || parsed.pageKind !== value.pageKind) return null;
  return {
    ...base(),
    type: 'canvas.routeChanged',
    ...session,
    path: parsed.path,
    pageKind: parsed.pageKind,
    ...history,
  };
}

export function parseWebsiteCanvasMessage(
  value: unknown,
  options: WebsiteCanvasParseOptions = {},
): WebsiteCanvasCanvasToHostMessage | null {
  if (!isRecord(value) || !isBase(value)) return null;
  if (value.type === 'canvas.websiteReady') {
    if (!hasExactKeys(value, [...BASE_KEYS, 'canvasInstanceId'])) return null;
    if (typeof value.canvasInstanceId !== 'string') return null;
    if (!SESSION_VALUE_RE.test(value.canvasInstanceId)) return null;
    return { ...base(), type: 'canvas.websiteReady', canvasInstanceId: value.canvasInstanceId };
  }
  if (value.type === 'canvas.websiteError') {
    if (!hasExactKeys(value, [...BASE_KEYS, 'code'])) return null;
    const code = ERROR_CODES.find((candidate) => candidate === value.code);
    return code ? { ...base(), type: 'canvas.websiteError', code } : null;
  }
  if (!hasSession(value)) return null;
  const session = { sessionId: value.sessionId as string, nonce: value.nonce as string };

  switch (value.type) {
    case 'canvas.websiteAck':
      if (!hasExactKeys(value, [...SESSION_KEYS, 'sequence'])) return null;
      if (!isSequence(value.sequence)) return null;
      return { ...base(), type: 'canvas.websiteAck', ...session, sequence: value.sequence };
    case 'canvas.routeChanged':
      return parseRouteChanged(value, session, options);
    case 'canvas.regionsChanged': {
      if (!hasExactKeys(value, [...SESSION_KEYS, 'pageKind', 'regions'])) return null;
      if (!isWebsitePageKind(value.pageKind)) return null;
      if (!Array.isArray(value.regions) || value.regions.length > WEBSITE_CANVAS_MAX_REGIONS) {
        return null;
      }
      const regions: WebsiteCanvasRegionReport[] = [];
      const seen = new Set<string>();
      for (const item of value.regions) {
        const report = parseRegionReport(item);
        if (!report || seen.has(report.regionId)) return null;
        seen.add(report.regionId);
        regions.push(report);
      }
      return {
        ...base(),
        type: 'canvas.regionsChanged',
        ...session,
        pageKind: value.pageKind,
        regions,
      };
    }
    case 'canvas.regionEditIntent':
      if (!hasExactKeys(value, [...SESSION_KEYS, 'regionId'])) return null;
      if (!isWebsiteEditRegionId(value.regionId)) return null;
      return { ...base(), type: 'canvas.regionEditIntent', ...session, regionId: value.regionId };
    case 'canvas.navigationBlocked':
      if (!hasExactKeys(value, [...SESSION_KEYS, 'reason']) || value.reason !== 'locked') {
        return null;
      }
      return { ...base(), type: 'canvas.navigationBlocked', ...session, reason: 'locked' };
    default:
      return null;
  }
}

/** True only for the session the host opened; stale sessions are ignored. */
export function isWebsiteCanvasSessionMessage(
  message: WebsiteCanvasSession,
  session: WebsiteCanvasSession,
): boolean {
  return message.sessionId === session.sessionId && message.nonce === session.nonce;
}

/** https everywhere except localhost; no credentials, query or fragment. */
export function normalizeWebsiteCanvasOrigin(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed || !checkMerchantText(trimmed, { maxLength: 512 }).ok) {
    throw new Error('Invalid website canvas origin');
  }
  const parsed = new URL(trimmed);
  if (parsed.username || parsed.password || parsed.search || parsed.hash) {
    throw new Error('Invalid website canvas origin');
  }
  const local = parsed.protocol === 'http:' && isLocalHostname(parsed.hostname);
  if (parsed.protocol !== 'https:' && !local) throw new Error('Invalid website canvas origin');
  return parsed.origin;
}

export interface WebsiteCanvasOriginsInput {
  readonly currentOrigin: string;
  readonly publicAdminUrl: string | null | undefined;
  readonly publicStorefrontUrl: string;
  readonly mode: 'admin-host' | 'storefront-receiver';
}

export type WebsiteCanvasOrigins =
  | { readonly ok: true; readonly adminOrigin: string; readonly storefrontOrigin: string }
  | { readonly ok: false };

/**
 * Both origins must be configured, distinct, and the current window must be
 * the one its role says it is. There is no derived or default admin origin.
 */
export function resolveWebsiteCanvasOrigins(
  input: WebsiteCanvasOriginsInput,
): WebsiteCanvasOrigins {
  try {
    if (!input.publicAdminUrl) return { ok: false };
    const adminOrigin = normalizeWebsiteCanvasOrigin(input.publicAdminUrl);
    const storefrontOrigin = normalizeWebsiteCanvasOrigin(input.publicStorefrontUrl);
    const currentOrigin = normalizeWebsiteCanvasOrigin(input.currentOrigin);
    if (adminOrigin === storefrontOrigin) return { ok: false };
    const expected = input.mode === 'admin-host' ? adminOrigin : storefrontOrigin;
    if (currentOrigin !== expected) return { ok: false };
    return { ok: true, adminOrigin, storefrontOrigin };
  } catch {
    return { ok: false };
  }
}

export interface WebsiteCanvasMessageEventLike {
  readonly origin: string;
  readonly source: unknown;
}

/** A message counts only from the exact expected origin AND window. */
export function isTrustedWebsiteCanvasEvent(args: {
  readonly event: WebsiteCanvasMessageEventLike;
  readonly expectedOrigin: string;
  readonly expectedSource: unknown;
}): boolean {
  return (
    args.expectedOrigin !== '' &&
    args.event.origin === args.expectedOrigin &&
    args.expectedSource !== null &&
    args.expectedSource !== undefined &&
    args.event.source === args.expectedSource
  );
}

function randomSessionValue(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export interface WebsiteCanvasInitInput {
  readonly storeSlug: string;
  readonly mode: WebsiteCanvasMode;
  readonly adminLocale: WebsiteInterfaceLocale;
  readonly initialPath: string;
  readonly editMode: boolean;
  readonly overlayLabels: WebsiteCanvasOverlayLabels;
  readonly draftBrowseGrant: WebsiteCanvasDraftBrowseGrant | null;
}

/**
 * The factories build messages, they do not validate them. Every receiver
 * parses what arrives, and a message its parser refuses is dropped.
 */
export const websiteCanvasBridge = {
  createSession(): WebsiteCanvasSession {
    return { sessionId: randomSessionValue(), nonce: randomSessionValue() };
  },
  normalizeOrigin: normalizeWebsiteCanvasOrigin,
  resolveOrigins: resolveWebsiteCanvasOrigins,
  isTrustedMessageEvent: isTrustedWebsiteCanvasEvent,
  canvasPath(storeSlug: string): string {
    return `/s/${encodeURIComponent(storeSlug)}/${WEBSITE_CANVAS_PATH_SUFFIX}`;
  },
  makeInitMessage(
    session: WebsiteCanvasSession,
    input: WebsiteCanvasInitInput,
  ): WebsiteCanvasHostInitMessage {
    return {
      ...base(),
      type: 'host.websiteInit',
      ...session,
      storeSlug: input.storeSlug,
      mode: input.mode,
      adminLocale: input.adminLocale,
      initialPath: input.initialPath,
      editMode: input.editMode,
      overlayLabels: input.overlayLabels,
      draftBrowseGrant: input.draftBrowseGrant,
    };
  },
  makeDraftBrowseGrantMessage(
    session: WebsiteCanvasSession,
    grant: WebsiteCanvasDraftBrowseGrant,
  ): WebsiteCanvasHostDraftBrowseGrantMessage {
    return { ...base(), type: 'host.draftBrowseGrant', ...session, grant };
  },
  makePageMessage(
    session: WebsiteCanvasSession,
    sequence: number,
    payload: WebsiteCanvasPagePayload,
  ): WebsiteCanvasHostPageMessage {
    return { ...base(), type: 'host.page', ...session, sequence, payload };
  },
  makeEditModeMessage(
    session: WebsiteCanvasSession,
    enabled: boolean,
  ): WebsiteCanvasHostEditModeMessage {
    return { ...base(), type: 'host.editMode', ...session, enabled };
  },
  makeNavigateMessage(
    session: WebsiteCanvasSession,
    target: WebsiteCanvasNavigateTarget,
  ): WebsiteCanvasHostNavigateMessage {
    return { ...base(), type: 'host.navigate', ...session, target };
  },
  makeFocusRegionMessage(
    session: WebsiteCanvasSession,
    regionId: string,
  ): WebsiteCanvasHostFocusRegionMessage {
    return { ...base(), type: 'host.focusRegion', ...session, regionId };
  },
  makeInteractionLockMessage(
    session: WebsiteCanvasSession,
    locked: boolean,
  ): WebsiteCanvasHostInteractionLockMessage {
    return { ...base(), type: 'host.interactionLock', ...session, locked };
  },
  makeReadyMessage(canvasInstanceId: string): WebsiteCanvasReadyMessage {
    return { ...base(), type: 'canvas.websiteReady', canvasInstanceId };
  },
  makeAckMessage(session: WebsiteCanvasSession, sequence: number): WebsiteCanvasAckMessage {
    return { ...base(), type: 'canvas.websiteAck', ...session, sequence };
  },
  makeRouteChangedMessage(
    session: WebsiteCanvasSession,
    route: {
      readonly path: string;
      readonly pageKind: WebsitePageKind | null;
      readonly canGoBack: boolean;
      readonly canGoForward: boolean;
    },
  ): WebsiteCanvasRouteChangedMessage {
    return {
      ...base(),
      type: 'canvas.routeChanged',
      ...session,
      path: route.path,
      pageKind: route.pageKind,
      canGoBack: route.canGoBack,
      canGoForward: route.canGoForward,
    };
  },
  makeRegionsChangedMessage(
    session: WebsiteCanvasSession,
    pageKind: WebsitePageKind,
    regions: readonly WebsiteCanvasRegionReport[],
  ): WebsiteCanvasRegionsChangedMessage {
    return { ...base(), type: 'canvas.regionsChanged', ...session, pageKind, regions };
  },
  makeRegionEditIntentMessage(
    session: WebsiteCanvasSession,
    regionId: string,
  ): WebsiteCanvasRegionEditIntentMessage {
    return { ...base(), type: 'canvas.regionEditIntent', ...session, regionId };
  },
  makeNavigationBlockedMessage(
    session: WebsiteCanvasSession,
  ): WebsiteCanvasNavigationBlockedMessage {
    return { ...base(), type: 'canvas.navigationBlocked', ...session, reason: 'locked' };
  },
  makeErrorMessage(code: WebsiteCanvasErrorCode): WebsiteCanvasErrorMessage {
    return { ...base(), type: 'canvas.websiteError', code };
  },
} as const;
